import 'dayjs/locale/lt';

import { Box, useTheme } from '@mui/material';
import {
 CategoryScale,
 Chart as ChartJS,
 Legend,
 LinearScale,
 LineElement,
 PointElement,
 Title,
 Tooltip,
} from 'chart.js';
import dayjs from 'dayjs';
import React, { useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';

import { WidgetItemType } from '../Dashboard.types';

ChartJS.register(
 CategoryScale,
 LinearScale,
 PointElement,
 LineElement,
 Title,
 Tooltip,
 Legend,
);

interface DashboardWidgetLineChartProps {
 widgetContent: WidgetItemType[];
}

const DashboardWidgetLineChart = ({
 widgetContent,
}: DashboardWidgetLineChartProps) => {
 const { t } = useTranslation();
 const theme = useTheme();

 const chartData = useMemo(() => {
  if (!widgetContent || widgetContent.length === 0) {
   return null;
  }

  // Extract unique dates from all items
  const allDates = new Set<string>();
  widgetContent.forEach((item) => {
   item.Items.forEach((entry) => {
    if (entry.Date) {
     const dateStr = dayjs(entry.Date).format('MM-DD');
     allDates.add(dateStr);
    }
   });
  });

  const sortedDates = Array.from(allDates).sort();

  // Create datasets for each group (Key)
  const datasets = widgetContent.map((item, index) => {
   // Use MUI theme palette for colors
   // We'll rotate through primary, secondary, error, warning, info, success for distinct datasets
   const chartColors = [
    theme.palette.primary.main,
    theme.palette.success.main,
    theme.palette.warning.main,
    theme.palette.info.main,
    theme.palette.error.main,
    // theme.palette.secondary.main,
   ];

   const color = chartColors[index % chartColors.length];

   // Map dates to values for this dataset
   const data = sortedDates.map((date) => {
    const matchingItem = item.Items.find((entry) => {
     if (!entry.Date) return false;
     const entryDate = dayjs(entry.Date).format('MM-DD');
     return entryDate === date;
    });

    return matchingItem?.Neto ?? 0;
   });

   return {
    label: item.Key,
    data,
    borderColor: color,
    backgroundColor: `${color}40`,
    tension: 0.1,
   };
  });

  return {
   labels: sortedDates,
   datasets,
  };
 }, [widgetContent]);

 if (!chartData) {
  return null;
 }

 const options = useMemo(
  () => ({
   responsive: true,
   maintainAspectRatio: false,
   plugins: {
    legend: {
     position: 'top' as const,
     labels: {
      boxWidth: 12,
      padding: 8,
      font: {
       size: 12,
      },
     },
    },
    tooltip: {
     mode: 'index' as const,
     intersect: false,
    },
   },
   scales: {
    x: {
     display: true,
     title: {
      display: true,
      text: t('dashboard.charts.date'),
     },
    },
    y: {
     display: true,
     title: {
      display: true,
      text: t('dashboard.charts.neto'),
     },
     beginAtZero: true,
    },
   },
  }),
  [t],
 );

 return (
  <Box
   sx={{
    height: '100%',
    width: '100%',
    position: 'relative',
   }}
  >
   <Line data={chartData} options={options} />
  </Box>
 );
};

export default DashboardWidgetLineChart;
